import React, { useRef, useState } from "react";
import { m } from "framer-motion";
import {
  ArrowRight,
  BarChart3,
  BookOpen,
  Check,
  CheckCircle2,
  CircleDot,
  Database,
  FileCheck2,
  FileText,
  Fingerprint,
  Globe2,
  GraduationCap,
  LayoutDashboard,
  LockKeyhole,
  MonitorSmartphone,
  RadioTower,
  School,
  ShieldCheck,
  Sparkles,
  UserCheck,
  Users,
  Workflow,
} from "lucide-react";
import { moduleExampleProfileFor } from "./moduleExampleProfiles";
import { ProductEvidencePanel, productEvidenceForModule } from "./productEvidence";
import "./ModuleExampleShowcase.css";

function Mark({ icon: Icon = CircleDot, small = false }) {
  return (
    <span className="module-example__mark" data-size={small ? "small" : "default"} aria-hidden="true">
      <Icon />
    </span>
  );
}

function WindowBar({ label, icon = LayoutDashboard, trailing }) {
  return (
    <div className="module-example__window-bar">
      <div><Mark icon={icon} small /><strong>{label}</strong></div>
      {trailing ? <span>{trailing}</span> : <i aria-hidden="true"><b /><b /><b /></i>}
    </div>
  );
}

function ExampleList({ items, icon = CheckCircle2, label = "Illustrated capability fields" }) {
  return (
    <ul className="module-example__list" aria-label={label}>
      {items.map((item) => (
        <li key={item}>
          <Mark icon={icon} small />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function EvaluationRail({ example, limit = 3 }) {
  return (
    <ol className="module-example__workflow" aria-label={`${example.moduleName} evaluation lenses`}>
      {example.evaluationLenses.slice(0, limit).map((step, index) => (
        <li key={step.id || step.title}>
          <span>{String(index + 1).padStart(2, "0")}</span>
          <div><strong>{step.title}</strong><p>{step.detail}</p></div>
        </li>
      ))}
    </ol>
  );
}

function DashboardPreview({ example }) {
  return (
    <div className="module-example__window module-example__dashboard" data-preview="dashboard">
      <WindowBar label={example.previewTitle} icon={BarChart3} trailing="Illustrative current view" />
      <div className="module-example__dashboard-layout">
        <aside aria-label="Example dashboard navigation">
          <strong>{example.workspace}</strong>
          {["Overview", "Review queue", "Reports"].map((label, index) => (
            <span key={label} data-active={index === 0 ? "true" : "false"}>{label}</span>
          ))}
        </aside>
        <div className="module-example__dashboard-main">
          <header><div><span>{example.state.label}</span><h3>{example.moduleName}</h3></div><span>Current school context</span></header>
          <div className="module-example__signal-grid">
            {example.items.slice(0, 3).map((item, index) => (
              <article key={item}>
                <span>Signal {String(index + 1).padStart(2, "0")}</span>
                <strong>{item}</strong>
                <small>Illustrative signal label · value not shown</small>
              </article>
            ))}
          </div>
          <div className="module-example__chart-row">
            <div className="module-example__chart" role="img" aria-label="Illustrative trend chart with no data values shown">
              <span>Illustrative trend shape</span>
              <div aria-hidden="true"><i /><i /><i /><i /><i /><i /></div>
            </div>
            <div className="module-example__decision-note">
              <Mark icon={CheckCircle2} />
              <span><small>{example.outcome.label}</small><strong>{example.outcome.value}</strong></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function WorkbenchPreview({ example }) {
  return (
    <div className="module-example__window module-example__workbench" data-preview="workbench">
      <WindowBar label={example.previewTitle} icon={Database} trailing={example.workspace} />
      <div className="module-example__workbench-layout">
        <EvaluationRail example={example} />
        <div className="module-example__record">
          <header>
            <div><span>Example record</span><h3>{example.moduleName}</h3></div>
            <span><CircleDot aria-hidden="true" />Authorised view</span>
          </header>
          <ExampleList items={example.items} />
          <footer><Mark icon={ShieldCheck} small /><span>{example.outcome.value}</span></footer>
        </div>
      </div>
    </div>
  );
}

function ReportCardArtifact({ example }) {
  return (
    <article className="module-example__paper module-example__report-card" aria-label={`${example.moduleName} illustrative report-card artifact`}>
      <header>
        <span className="module-example__paper-seal" aria-hidden="true"><GraduationCap /></span>
        <div><small>Illustrative school document</small><h3>Vidyapeeth Demo School</h3><p>{example.document.title}</p></div>
        <span>Sample</span>
      </header>
      <dl className="module-example__student-fields">
        <div><dt>Learner</dt><dd>Sample learner</dd></div>
        <div><dt>Class</dt><dd>Class · Section</dd></div>
        <div><dt>Reporting period</dt><dd>Selected term</dd></div>
      </dl>
      <div className="module-example__marks-table" role="table" aria-label="Illustrative report fields — no school data shown">
        <div role="row"><span role="columnheader">Learning record</span><span role="columnheader">Evidence</span><span role="columnheader">Review</span></div>
        {example.items.slice(0, 3).map((item) => (
          <div role="row" key={item}>
            <span role="cell">{item}</span><span role="cell">School record</span><span role="cell"><Check aria-hidden="true" />Authorised review</span>
          </div>
        ))}
      </div>
      <div className="module-example__report-summary" aria-label="Illustrative report-card supporting records">
        <div><small>Attendance</small><strong>School-recorded attendance evidence</strong></div>
        <div><small>Co-scholastic</small><strong>Teacher-reviewed school record</strong></div>
        <div><small>Grading legend</small><strong>School-configured bands and pass rules</strong></div>
      </div>
      <div className="module-example__teacher-note">
        <strong>Publication note</strong>
        <p>{example.document.review}</p>
      </div>
      <footer><span>Value not shown</span><strong>Illustrative printable layout</strong></footer>
    </article>
  );
}

function GeneralDocumentArtifact({ example }) {
  return (
    <article className="module-example__paper module-example__general-document" aria-label={`${example.moduleName} illustrative document artifact`}>
      <header>
        <span className="module-example__paper-seal" aria-hidden="true"><School /></span>
        <div><small>Vidyapeeth Demo School</small><h3>{example.document.title}</h3><p>{example.moduleName}</p></div>
        <span>Sample</span>
      </header>
      <div className="module-example__document-hero">
        <Mark icon={FileCheck2} />
        <span><small>{example.state.label}</small><strong>{example.state.value}</strong></span>
      </div>
      <dl className="module-example__document-lines">
        {example.items.slice(0, 3).map((item, index) => (
          <div key={item}><dt>Field {String(index + 1).padStart(2, "0")}</dt><dd>{item}</dd></div>
        ))}
      </dl>
      <div className="module-example__teacher-note"><strong>Review boundary</strong><p>{example.document.review}</p></div>
      <footer><span>No customer record</span><strong>Illustrative printable layout</strong></footer>
    </article>
  );
}

function CertificateArtifact({ example }) {
  return (
    <article className="module-example__paper module-example__certificate" aria-label="Sample certificate layout with placeholder recipient, serial and signatory fields">
      <header>
        <span className="module-example__paper-seal" aria-hidden="true"><GraduationCap /></span>
        <div><small>Vidyapeeth Demo School</small><h3>Certificate</h3><p>{example.document.title}</p></div>
        <span>Sample</span>
      </header>
      <div className="module-example__certificate-body">
        <small>This certifies that</small>
        <strong>Sample recipient</strong>
        <p>The selected certificate template and school-approved issue fields appear here before generation.</p>
      </div>
      <dl className="module-example__certificate-meta">
        <div><dt>Certificate serial</dt><dd>School-issued reference</dd></div>
        <div><dt>Public verification</dt><dd>QR and revocation status</dd></div>
        <div><dt>Authorisation</dt><dd>Approved signatory</dd></div>
      </dl>
      <footer><span>No customer record</span><strong>{example.document.review}</strong></footer>
    </article>
  );
}

function IdentityCardArtifact({ example }) {
  return (
    <div className="module-example__id-proof" role="img" aria-label="Sample student ID card: school name, learner name, class and section, admission number and validity, all shown as placeholder fields, with a print-review note">
      <article>
        <header><School aria-hidden="true" /><span><strong>Vidyapeeth Demo School</strong><small>Identity card · Sample</small></span></header>
        <div className="module-example__id-person"><span aria-hidden="true"><Users /></span><div><small>Learner</small><strong>Sample learner</strong><p>Class · Section</p></div></div>
        <dl><div><dt>Admission number</dt><dd>Sample field</dd></div><div><dt>Validity</dt><dd>School-defined</dd></div></dl>
        <footer><span>School-configured QR or reference field</span><b aria-hidden="true" /></footer>
      </article>
      <div><small>Print review</small><strong>{example.document.review}</strong><p>{example.outcome.value}</p></div>
    </div>
  );
}

function ReceiptArtifact({ example }) {
  return (
    <article className="module-example__paper module-example__receipt" aria-label="Sample fee receipt layout with placeholder receipt number, invoice link and amounts">
      <header>
        <span className="module-example__paper-seal" aria-hidden="true"><FileCheck2 /></span>
        <div><small>Vidyapeeth Demo School</small><h3>Fee receipt</h3><p>Recorded collection proof · Sample</p></div>
        <span>Sample</span>
      </header>
      <dl className="module-example__receipt-meta"><div><dt>Receipt number</dt><dd>Sequential school reference</dd></div><div><dt>Invoice</dt><dd>Linked invoice record</dd></div></dl>
      <div className="module-example__receipt-lines" role="table" aria-label="Illustrative receipt fields — no school data shown">
        <div role="row"><span role="columnheader">Recorded item</span><span role="columnheader">Evidence</span><span role="columnheader">Amount</span></div>
        {example.items.slice(0, 3).map((item) => <div role="row" key={item}><span role="cell">{item}</span><span role="cell">School record</span><span role="cell">Value not shown</span></div>)}
      </div>
      <div className="module-example__teacher-note"><strong>Collection boundary</strong><p>{example.document.review}</p></div>
      <footer><span>No payment or customer data</span><strong>Illustrative printable receipt</strong></footer>
    </article>
  );
}

function DocumentArtifact({ example }) {
  switch (example.document?.type) {
    case "report-card": return <ReportCardArtifact example={example} />;
    case "certificate": return <CertificateArtifact example={example} />;
    case "identity-card": return <IdentityCardArtifact example={example} />;
    case "receipt": return <ReceiptArtifact example={example} />;
    default: return <GeneralDocumentArtifact example={example} />;
  }
}

function DocumentPreview({ example }) {
  return (
    <div className="module-example__document-stage" data-preview="document">
      <div className="module-example__document-toolbar">
        <span><FileText aria-hidden="true" />{example.document?.title}</span>
        <span>Preview · Review · Issue</span>
      </div>
      <DocumentArtifact example={example} />
    </div>
  );
}

function ReportPreview({ example }) {
  return (
    <div className="module-example__window module-example__report" data-preview="report">
      <WindowBar label={example.previewTitle} icon={BarChart3} trailing="Filter-aware example" />
      <div className="module-example__report-tools">
        <span>School scope</span><span>Selected period</span><span>Authorised columns</span>
      </div>
      <div className="module-example__report-table" role="table" aria-label={`${example.moduleName} illustrative report structure`}>
        <div role="row"><span role="columnheader">Evidence field</span><span role="columnheader">Source</span><span role="columnheader">Review state</span></div>
        {example.items.map((item) => (
          <div role="row" key={item}>
            <span role="cell">{item}</span><span role="cell">Scoped school record</span><span role="cell"><CircleDot aria-hidden="true" />Reviewable</span>
          </div>
        ))}
      </div>
      <footer><Mark icon={FileCheck2} small /><span>{example.outcome.value}</span></footer>
    </div>
  );
}

function SitePreview({ example }) {
  const site = example.site;
  return (
    <div
      className="module-example__site-editor"
      data-preview="site"
      data-testid="website-builder-story-visual"
      role="group"
      aria-label="Website Builder editor with a concept site for a fictional demo school"
    >
      <aside aria-label="Illustrative website editor outline">
        <header><Mark icon={Globe2} small /><strong>Site editor</strong></header>
        <div><span>Pages</span>{site.nav.map((item, index) => <b key={item} data-active={index === 0 ? "true" : "false"}>{item}</b>)}</div>
        <div><span>Selected section</span><b data-active="true">Campus hero</b><b>Admissions action</b></div>
        <footer><ShieldCheck aria-hidden="true" />Human review before publishing</footer>
      </aside>
      <div className="module-example__site-preview">
        <div className="module-example__browser-bar"><i aria-hidden="true"><b /><b /><b /></i><span>demo-school.example</span><em>Live preview</em></div>
        <article>
          <div className="module-example__site-nav">
            <strong><School aria-hidden="true" />{site.name}</strong>
            <div>{site.nav.slice(0, 3).map((item) => <span key={item}>{item}</span>)}</div>
            <b>Enquire</b>
          </div>
          <div className="module-example__site-hero">
            <img
              src={site.image.src}
              srcSet={site.image.srcSet}
              sizes={site.image.sizes}
              alt={site.image.alt}
              width={site.image.width}
              height={site.image.height}
              loading="lazy"
              decoding="async"
              draggable="false"
              data-image-treatment="responsive-unfiltered-uncropped"
              data-testid="website-builder-campus-visual"
            />
            <span className="module-example__site-shade" aria-hidden="true" />
            <div className="module-example__site-copy">
              <span>{site.eyebrow} · Fictional concept</span>
              <h3>{site.headline}</h3>
              <p>{site.supporting}</p>
              <b>{site.action}<ArrowRight aria-hidden="true" /></b>
            </div>
          </div>
          <div className="module-example__site-highlights">
            {["A clear admissions path", "School stories and updates", "Enquiries connected to CRM"].map((item, index) => (
              <div key={item}><span>0{index + 1}</span><strong>{item}</strong></div>
            ))}
          </div>
        </article>
        <footer><CircleDot aria-hidden="true" />{site.disclosure}</footer>
      </div>
    </div>
  );
}

function WebsiteBuilderEvidenceFallback({ example }) {
  return (
    <div className="module-example__site-evidence-fallback">
      <span>Accessible capture summary</span>
      <h3>From editor controls to a reviewed published school site</h3>
      <ol>
        <li><strong>Editor view</strong><p>Page navigation, section controls, live preview and the selected hero inspector use the same fictional demo fixture.</p></li>
        <li><strong>Published result</strong><p>Admissions navigation, the school story and enquiry action are shown in the corresponding public-site route.</p></li>
        <li><strong>School outcome</strong><p>{example.outcome.value}</p></li>
      </ol>
    </div>
  );
}

function WebsiteBuilderResult({ example, productEvidence }) {
  return (
    <div className="module-example__website-result" data-testid="website-builder-result-evidence">
      <ol className="module-example__website-result-steps" aria-label="How this Website Builder example was produced, in three steps">
        <li><span>01</span><strong>Fictional site direction</strong><small>Shown in Example view</small></li>
        <li><span>02</span><strong>Build and review</strong><small>Shipped Website Builder route</small></li>
        <li><span>03</span><strong>Publish the outcome</strong><small>Shipped Public Site route</small></li>
      </ol>
      <ProductEvidencePanel
        evidence={productEvidence}
        fallback={<WebsiteBuilderEvidenceFallback example={example} />}
      />
      <div className="module-example__website-result-note">
        <Mark icon={CheckCircle2} small />
        <span><small>{example.outcome.label}</small><strong>{example.outcome.value}</strong></span>
      </div>
    </div>
  );
}

function MobilePreview({ example }) {
  return (
    <div className="module-example__mobile-stage" data-preview="mobile">
      <div className="module-example__phone" role="img" aria-label={`${example.moduleName} illustrative mobile screen`}>
        <span className="module-example__phone-speaker" aria-hidden="true" />
        <header><Mark icon={MonitorSmartphone} small /><span><small>{example.workspace}</small><strong>{example.moduleName}</strong></span></header>
        <div className="module-example__phone-summary"><small>{example.state.label}</small><strong>{example.state.value}</strong></div>
        <ExampleList items={example.items.slice(0, 3)} label={`${example.moduleName} illustrative mobile actions`} />
        <div className="module-example__phone-nav"><span data-active="true">Home</span><span>Updates</span><span>Profile</span></div>
      </div>
      <div className="module-example__mobile-notes">
        <span>Example user view</span><h3>Important school work, sized for a phone.</h3>
        <p>{example.previewSummary}</p>
        <ExampleList items={example.roles.slice(0, 3)} icon={Users} label="Typical users shown in this example" />
      </div>
    </div>
  );
}

function ProviderPreview({ example }) {
  const icons = [Database, RadioTower, FileCheck2];
  return (
    <div className="module-example__pipeline" data-preview="provider">
      <WindowBar label={example.previewTitle} icon={RadioTower} trailing="Example provider evidence" />
      <ol aria-label={`${example.moduleName} illustrative evidence layers`}>
        {example.items.slice(0, 3).map((item, index) => (
          <li key={item}>
            <Mark icon={icons[index]} />
            <span><small>Evidence layer {String(index + 1).padStart(2, "0")}</small><strong>{item}</strong></span>
          </li>
        ))}
      </ol>
      <div className="module-example__provider-evidence">
        <span><ShieldCheck aria-hidden="true" />The configured provider's returned state remains distinct from the school record.</span>
        <ExampleList items={example.items.slice(0, 3)} label="Illustrative provider evidence" />
      </div>
    </div>
  );
}

function DevicePreview({ example }) {
  const icons = [Fingerprint, LockKeyhole, CheckCircle2];
  return (
    <div className="module-example__device-stage" data-preview="device">
      <WindowBar label={example.previewTitle} icon={Fingerprint} trailing="Illustrative device event" />
      <div className="module-example__device-event">
        <header><span>Incoming event</span><strong>{example.moduleName}</strong><b><CircleDot aria-hidden="true" />Example only</b></header>
        <ol aria-label={`${example.moduleName} illustrative event evidence`}>
          {example.items.slice(0, 3).map((item, index) => (
            <li key={item}><Mark icon={icons[index]} /><span><small>Event evidence {String(index + 1).padStart(2, "0")}</small><strong>{item}</strong></span></li>
          ))}
        </ol>
      </div>
      {example.prerequisites.length ? (
        <div className="module-example__requirements"><strong>Rollout conditions</strong>{example.prerequisites.slice(0, 2).map((item) => <span key={item}><ShieldCheck aria-hidden="true" />{item}</span>)}</div>
      ) : null}
    </div>
  );
}

function AutomationPreview({ example }) {
  const nodeLabels = ["Trigger", "Conditions", "Reviewed action", "Run evidence"];
  return (
    <div className="module-example__automation-stage" data-preview="automation">
      <WindowBar label={example.previewTitle} icon={Workflow} trailing="Illustrative rule canvas" />
      <div className="module-example__automation-canvas">
        {example.items.map((item, index) => (
          <React.Fragment key={item}>
            <article><span>0{index + 1} · {nodeLabels[index]}</span><strong>{item}</strong>{index === 2 ? <small><UserCheck aria-hidden="true" />Human review boundary</small> : null}</article>
            {index < example.items.length - 1 ? <ArrowRight aria-hidden="true" /> : null}
          </React.Fragment>
        ))}
      </div>
      <footer><FileCheck2 aria-hidden="true" /><span><small>{example.output.label}</small><strong>{example.outcome.value}</strong></span></footer>
    </div>
  );
}

function AiPreview({ example }) {
  const workflowIcons = [LockKeyhole, Sparkles, UserCheck];
  return (
    <div className="module-example__ai-stage" data-preview="ai">
      <div className="module-example__ai-prompt">
        <span><Sparkles aria-hidden="true" />{example.aiBoundary?.label || "Governed AI capability"}</span>
        <h3>{example.moduleName}</h3>
        <p>{example.previewSummary}</p>
        <div><LockKeyhole aria-hidden="true" /><span><small>Scope</small><strong>Only supported, permission-checked school context</strong></span></div>
      </div>
      <ol className="module-example__ai-flow" aria-label={`${example.moduleName} evaluation lenses`}>
        {example.evaluationLenses.slice(0, 3).map((step, index) => (
          <li key={step.id || step.title}><Mark icon={workflowIcons[index]} /><span><small>{step.title}</small><strong>{step.detail}</strong></span></li>
        ))}
      </ol>
      <div className="module-example__ai-result"><ShieldCheck aria-hidden="true" /><span><small>{example.outcome.label}</small><strong>{example.outcome.value}</strong></span></div>
    </div>
  );
}

function TimetableGridPreview({ example }) {
  const days = example.presentation.labels;
  const entries = example.presentation.sampleItems;
  return (
    <div className="module-example__window module-example__timetable" data-preview="timetable-grid">
      <WindowBar label={example.presentation.title} icon={LayoutDashboard} trailing="Illustrative weekly grid" />
      <div className="module-example__schedule-meta"><span>Selected class and section</span><span>Academic week</span><span>Conflict review</span></div>
      <div className="module-example__timetable-grid" role="table" aria-label="Illustrative weekly timetable — no school data shown">
        <div role="row"><span role="columnheader">Period</span>{days.map((day) => <span role="columnheader" key={day}>{day}</span>)}</div>
        {entries.slice(0, 3).map((item, rowIndex) => (
          <div role="row" key={item}>
            <span role="rowheader">Period {rowIndex + 1}</span>
            {days.map((day, dayIndex) => <span role="cell" key={day}><small>{dayIndex === 2 && rowIndex === 1 ? "Review" : "Scheduled field"}</small><strong>{entries[(rowIndex + dayIndex) % entries.length]}</strong></span>)}
          </div>
        ))}
      </div>
      <footer><ShieldCheck aria-hidden="true" /><span>{example.outcome.value}</span></footer>
    </div>
  );
}

function ExamDatesheetPreview({ example }) {
  const columns = example.presentation.labels;
  return (
    <div className="module-example__window module-example__datesheet" data-preview="exam-datesheet">
      <WindowBar label={example.presentation.title} icon={FileText} trailing="Illustrative datesheet" />
      <div className="module-example__datesheet-title"><span>Selected examination</span><strong>{example.moduleName}</strong><p>Dates, classes and resources remain school-configured.</p></div>
      <div className="module-example__datesheet-table" role="table" aria-label="Illustrative exam datesheet — no school data shown">
        <div role="row">{columns.map((column) => <span role="columnheader" key={column}>{column}</span>)}</div>
        {example.presentation.sampleItems.map((item) => (
          <div role="row" key={item}><span role="cell">{item}</span><span role="cell">Selected class and section</span><span role="cell">School-configured slot</span><span role="cell">School-configured maximum / pass marks</span></div>
        ))}
      </div>
      <footer><CheckCircle2 aria-hidden="true" /><span>{example.outcome.value}</span></footer>
    </div>
  );
}

function LessonDocumentPreview({ example }) {
  return (
    <div className="module-example__document-stage module-example__lesson-stage" data-preview="lesson-document">
      <div className="module-example__document-toolbar"><span><BookOpen aria-hidden="true" />{example.presentation.title}</span><span>Draft · Review · Share</span></div>
      <article className="module-example__paper module-example__lesson-paper">
        <header><span className="module-example__paper-seal"><BookOpen aria-hidden="true" /></span><div><small>Illustrative planning document</small><h3>{example.moduleName}</h3><p>Selected class · Subject · Lesson</p></div><span>Sample</span></header>
        <div className="module-example__lesson-sections">
          {example.presentation.labels.map((label, index) => <section key={label}><span>0{index + 1}</span><div><h4>{label}</h4><p>{example.items[index] || example.state.value}</p></div></section>)}
        </div>
        <footer><span>No teacher or learner data</span><strong>{example.outcome.value}</strong></footer>
      </article>
    </div>
  );
}

function FormBuilderPreview({ example }) {
  return (
    <div className="module-example__form-stage" data-preview="form-builder">
      <WindowBar label={example.presentation.title} icon={FileText} trailing="Illustrative form workspace" />
      <div className="module-example__form-layout">
        <aside><span>Field builder</span>{["Short text", "Choice", "Document", "Consent"].map((item) => <b key={item}>{item}</b>)}</aside>
        <article aria-label="Illustrative published custom form">
          <header><small>Published form preview</small><strong>School information form</strong><p>Fictional sample fields</p></header>
          {example.presentation.sampleItems.map((item, index) => <div className="module-example__form-field" key={item}><strong>Field {index + 1} · {item}</strong><span>Value not shown</span></div>)}
          <span className="module-example__form-submit" aria-hidden="true">Submit response</span>
        </article>
        <section aria-label="Illustrative custom form response review"><header><small>Response review</small><strong>Sample response</strong></header>{example.presentation.sampleItems.map((item) => <div key={item}><span>{item}</span><b>Value not shown</b></div>)}<footer><ShieldCheck aria-hidden="true" />Authorised review</footer></section>
      </div>
    </div>
  );
}

function PayrollPreview({ example }) {
  return (
    <div className="module-example__payroll-stage" data-preview="payroll-run">
      <WindowBar label={example.presentation.title} icon={FileCheck2} trailing="Illustrative payroll proof" />
      <div className="module-example__payroll-layout">
        <section><header><span>Payroll run</span><strong>Selected pay period</strong></header>{example.items.slice(0, 3).map((item, index) => <div key={item}><span>0{index + 1}</span><strong>{item}</strong><small>School record</small></div>)}<footer><UserCheck aria-hidden="true" />Authorised payroll review</footer></section>
        <article className="module-example__payslip"><header><School aria-hidden="true" /><span><small>Vidyapeeth Demo School</small><strong>Payslip · Sample employee</strong></span></header><dl>{example.presentation.labels.slice(0, 3).map((label) => <div key={label}><dt>{label}</dt><dd>Value not shown</dd></div>)}</dl><div><span>Net pay</span><strong>Value not shown</strong></div><footer>Illustrative payslip · no employee or payroll values</footer></article>
      </div>
    </div>
  );
}

function ExportTablePreview({ example }) {
  const columns = example.presentation.labels;
  return (
    <div className="module-example__window module-example__spreadsheet" data-preview="export-table">
      <WindowBar label={example.presentation.title} icon={FileCheck2} trailing="Illustrative spreadsheet" />
      <div className="module-example__spreadsheet-tools"><span>Selected report</span><span>Filters retained</span><span>CSV or XLSX according to the supported export</span></div>
      <div className="module-example__sheet" role="table" aria-label="Illustrative spreadsheet export — no school data shown">
        <div role="row"><span role="columnheader" />{columns.map((column, index) => <span role="columnheader" key={column}><small>{String.fromCharCode(65 + index)}</small>{column}</span>)}</div>
        {example.presentation.sampleItems.map((item, rowIndex) => <div role="row" key={item}><span role="rowheader">{rowIndex + 1}</span><span role="cell">{item}</span><span role="cell">Applied school scope</span><span role="cell">Authorised columns</span><span role="cell">Audit record</span></div>)}
      </div>
    </div>
  );
}

function FinanceLedgerPreview({ example }) {
  return (
    <div className="module-example__finance-stage" data-preview="finance-ledger">
      <WindowBar label={example.presentation.title} icon={FileCheck2} trailing="Illustrative finance evidence" />
      <div className="module-example__finance-layout">
        <ol aria-label={`${example.moduleName} illustrative finance evidence fields`}>
          {example.presentation.labels.map((label, index) => <li key={label}><span>0{index + 1}</span><div><small>{label}</small><strong>{example.items[index] || example.state.value}</strong></div></li>)}
        </ol>
        <section><header><ShieldCheck aria-hidden="true" /><span><small>Evidence boundary</small><strong>School record and provider evidence stay distinguishable.</strong></span></header><dl><div><dt>{example.state.label}</dt><dd>{example.state.value}</dd></div><div><dt>{example.output.label}</dt><dd>{example.output.value}</dd></div><div><dt>{example.outcome.label}</dt><dd>{example.outcome.value}</dd></div></dl><footer>No amount, settlement or reconciliation value is fabricated.</footer></section>
      </div>
    </div>
  );
}

const EXAMPLE_RENDERERS = Object.freeze({
  dashboard: DashboardPreview,
  workbench: WorkbenchPreview,
  document: DocumentPreview,
  report: ReportPreview,
  site: SitePreview,
  mobile: MobilePreview,
  provider: ProviderPreview,
  device: DevicePreview,
  automation: AutomationPreview,
  ai: AiPreview,
  "timetable-grid": TimetableGridPreview,
  "exam-datesheet": ExamDatesheetPreview,
  "lesson-document": LessonDocumentPreview,
  "form-builder": FormBuilderPreview,
  "payroll-run": PayrollPreview,
  "export-table": ExportTablePreview,
  "finance-ledger": FinanceLedgerPreview,
});

function ResultStrip({ example }) {
  return (
    <dl className="module-example__result-strip" aria-label={`${example.moduleName} example state, output and result`}>
      {[example.state, example.output, example.outcome].map((item, index) => {
        const icons = [CircleDot, FileCheck2, CheckCircle2];
        const Icon = icons[index];
        return <div key={item.label}><dt><Icon aria-hidden="true" />{item.label}</dt><dd>{item.value}</dd></div>;
      })}
    </dl>
  );
}

function WorkflowPanel({ example }) {
  return (
    <div className="module-example__workflow-panel">
      <div>
        <span>Evaluation lenses</span>
        <h3>How the school can evaluate this capability</h3>
        <p>These are evaluation lenses, not an invented click-by-click product workflow.</p>
        <EvaluationRail example={example} />
      </div>
      <aside>
        <section><span>Supported work</span><ExampleList items={example.items} /></section>
        {example.prerequisites.length ? <section><span>Before rollout</span><ExampleList items={example.prerequisites.slice(0, 3)} icon={ShieldCheck} label="Rollout requirements" /></section> : null}
        <section><span>Typical users</span><div className="module-example__role-chips">{example.roles.slice(0, 5).map((role) => <b key={role}>{role}</b>)}</div></section>
      </aside>
    </div>
  );
}

function ResultPanel({ example }) {
  return (
    <div className="module-example__result-panel">
      <div className="module-example__result-hero"><Mark icon={CheckCircle2} /><span><small>{example.outcome.label}</small><h3>{example.outcome.value}</h3><p>No sample metric, customer result or live product state is implied.</p></span></div>
      <ResultStrip example={example} />
    </div>
  );
}

const EXAMPLE_TABS = Object.freeze([
  Object.freeze({ id: "example", label: "Example view", Icon: LayoutDashboard }),
  Object.freeze({ id: "workflow", label: "Workflow", Icon: Workflow }),
  Object.freeze({ id: "result", label: "Result", Icon: CheckCircle2 }),
]);

export default function ModuleExampleShowcase({ mod, profile, narrative, exampleProfile, reduce = false, paused = false }) {
  const example = exampleProfile || moduleExampleProfileFor(mod, profile, narrative);
  const [selection, setSelection] = useState({ key: null, panel: "example", keyboard: false });
  const tabRefs = useRef([]);
  if (!example) return null;

  const Preview = EXAMPLE_RENDERERS[example.kind];
  if (!Preview) return null;
  const productEvidence = productEvidenceForModule(example.key);

  // React Router can reuse ModulePage while the :key param changes. Scope the local tab state to
  // the active catalogue key so a related-module navigation always opens on its example view.
  const selectionIsCurrent = selection.key === example.key;
  const activePanel = selectionIsCurrent ? selection.panel : "example";
  const keyboardSelection = selectionIsCurrent && selection.keyboard;
  const titleId = `module-example-${example.key}-title`;
  const captionId = `module-example-${example.key}-caption`;
  const panelId = `module-example-${example.key}-panel`;
  const activeTabId = `module-example-${example.key}-${activePanel}-tab`;
  const instant = reduce || paused;

  const selectTabFromKey = (event, index) => {
    let nextIndex = null;
    if (event.key === "ArrowRight") nextIndex = (index + 1) % EXAMPLE_TABS.length;
    if (event.key === "ArrowLeft") nextIndex = (index - 1 + EXAMPLE_TABS.length) % EXAMPLE_TABS.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = EXAMPLE_TABS.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    const next = EXAMPLE_TABS[nextIndex];
    setSelection({ key: example.key, panel: next.id, keyboard: true });
    tabRefs.current[nextIndex]?.focus();
  };

  return (
    <m.figure
      className="module-example"
      data-testid="module-example-showcase"
      data-example-kind={example.kind}
      data-example-key={example.key}
      data-source-type={example.sourceType}
      data-motion-paused={paused ? "true" : "false"}
      data-aira-fab-clearance="hide-compact"
      aria-labelledby={titleId}
      aria-describedby={captionId}
      initial={instant ? false : { opacity: 0, transform: "translate3d(0, 8px, 0)" }}
      whileInView={{ opacity: 1, transform: "translate3d(0, 0, 0)" }}
      viewport={{ once: true, amount: 0.12 }}
      transition={{ duration: instant ? 0 : 0.22, ease: [0.22, 1, 0.36, 1] }}
    >
      <header className="module-example__header">
        <div>
          <span><CircleDot aria-hidden="true" />Example view</span>
          <h2 id={titleId}>{example.previewTitle}</h2>
          <p>{example.previewSummary}</p>
        </div>
        <span className="module-example__badge"><Mark icon={mod?.Icon || BookOpen} small />{example.workspace}</span>
      </header>

      <div className="module-example__tabs" role="tablist" aria-label={`${example.moduleName} example details`}>
        {EXAMPLE_TABS.map((tab, index) => (
          <button
            key={tab.id}
            ref={(node) => { tabRefs.current[index] = node; }}
            id={`module-example-${example.key}-${tab.id}-tab`}
            type="button"
            role="tab"
            aria-selected={activePanel === tab.id}
            aria-controls={panelId}
            tabIndex={activePanel === tab.id ? 0 : -1}
            onClick={() => setSelection({ key: example.key, panel: tab.id, keyboard: false })}
            onKeyDown={(event) => selectTabFromKey(event, index)}
          >
            <tab.Icon aria-hidden="true" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="module-example__canvas">
        <m.div
          key={activePanel}
          id={panelId}
          role="tabpanel"
          aria-labelledby={activeTabId}
          className="module-example__panel"
          initial={instant || keyboardSelection ? false : { opacity: 0, transform: "translate3d(0, 6px, 0)" }}
          animate={{ opacity: 1, transform: "translate3d(0, 0, 0)" }}
          transition={{ duration: instant || keyboardSelection ? 0 : 0.18, ease: [0.22, 1, 0.36, 1] }}
        >
          {activePanel === "example" ? (
            example.key === "website-builder-ai"
              ? <Preview example={example} />
              : <ProductEvidencePanel evidence={productEvidence} fallback={<Preview example={example} />} />
          ) : null}
          {activePanel === "workflow" ? <WorkflowPanel example={example} /> : null}
          {activePanel === "result" ? (
            example.key === "website-builder-ai" && productEvidence
              ? <WebsiteBuilderResult example={example} productEvidence={productEvidence} />
              : <ResultPanel example={example} />
          ) : null}
        </m.div>
      </div>

      <figcaption id={captionId} className="module-example__caption">
        <span><ShieldCheck aria-hidden="true" />{example.disclosure}</span>
        <span>Example labels and outcomes come from this capability's reviewed catalogue record.</span>
      </figcaption>
    </m.figure>
  );
}

export { EXAMPLE_RENDERERS };
